import { useEffect, useState } from "react";
import { Keyboard, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Keyboard shortcut layer for the prescriber dashboard.
 *
 * Wraps the global keydown listener + a press-`?` help overlay. Yields
 * the currently-focused row index (J/K navigation) up to the caller via
 * `onFocusIndexChange` so the dashboard can render a focus ring on the
 * right row.
 *
 * Shortcuts (auto-disabled while typing in <input>/<textarea>/contenteditable):
 *   ?      toggle help overlay
 *   T      toggle triage mode
 *   J / ↓  focus next row
 *   K / ↑  focus previous row
 *   Enter  open the focused row's "View Details" button (clicks data-testid="view-order-<id>")
 *   A      open Write-ERX modal for focused row (clicks data-testid="approve-order-<id>")
 *   D      open Write-ERX modal for focused row, then auto-click the Deny
 *          sub-panel toggle (data-testid="open-deny-btn") once the modal mounts
 *   Esc    close any open modal AND release any held lock (handled by caller via existing modal close)
 *
 * Action shortcuts find the row by querying `data-testid^=order-row-` so they
 * keep working even if the dashboard re-renders or reorders rows.
 */
export default function PrescriberKeyboardShortcuts({
  enabled,
  rowIds,
  focusedIdx,
  onFocusIndexChange,
  onToggleTriage,
  onCloseModal,
  modalOpen,
}) {
  const [helpOpen, setHelpOpen] = useState(false);

  // One-shot discoverability toast: fires the first time a prescriber lands
  // on the Orders tab so they know the shortcut layer exists. Per-user via
  // localStorage; never fires again once dismissed/auto-dismissed.
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("glpflo_kbshortcut_hint_seen") === "1") return;
    const timer = setTimeout(() => {
      toast(
        "Tip: press ? for keyboard shortcuts",
        {
          description: "T = triage · J/K = focus rows · A/D = approve/deny · Esc = close",
          duration: 7000,
          action: {
            label: "Show me",
            onClick: () => setHelpOpen(true),
          },
        },
      );
      window.localStorage.setItem("glpflo_kbshortcut_hint_seen", "1");
    }, 1500);
    return () => clearTimeout(timer);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const clickByTestId = (testId) => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (el) { el.click(); return true; }
      return false;
    };

    const handler = (e) => {
      if (isTypingTarget(e.target)) return;
      const k = e.key;
      // Help overlay toggle — `?` is shift+/, accept either
      if (k === "?" || (e.shiftKey && k === "/")) {
        e.preventDefault();
        setHelpOpen((v) => !v);
        return;
      }
      // Esc always closes help + caller's modal
      if (k === "Escape") {
        if (helpOpen) { setHelpOpen(false); return; }
        if (modalOpen) { onCloseModal && onCloseModal(); return; }
      }
      // Don't intercept other shortcuts while help is open
      if (helpOpen) return;
      // T → triage toggle
      if (k === "t" || k === "T") {
        e.preventDefault();
        onToggleTriage && onToggleTriage();
        return;
      }
      // J / ArrowDown → next row
      if (k === "j" || k === "J" || k === "ArrowDown") {
        if (!rowIds || rowIds.length === 0) return;
        e.preventDefault();
        const next = Math.min((focusedIdx ?? -1) + 1, rowIds.length - 1);
        onFocusIndexChange(next);
        return;
      }
      // K / ArrowUp → previous row
      if (k === "k" || k === "K" || k === "ArrowUp") {
        if (!rowIds || rowIds.length === 0) return;
        e.preventDefault();
        const prev = Math.max((focusedIdx ?? 0) - 1, 0);
        onFocusIndexChange(prev);
        return;
      }
      // Action shortcuts require a focused row
      if (focusedIdx == null || !rowIds || !rowIds[focusedIdx]) return;
      const id = rowIds[focusedIdx];
      // Enter → open detail modal
      if (k === "Enter") {
        e.preventDefault();
        clickByTestId(`view-order-${id}`);
        return;
      }
      // A → open Write-ERX action modal for the focused row
      if (k === "a" || k === "A") {
        e.preventDefault();
        clickByTestId(`approve-order-${id}`);
        return;
      }
      // D → open Write-ERX modal then auto-open the Deny sub-panel
      if (k === "d" || k === "D") {
        e.preventDefault();
        if (clickByTestId(`approve-order-${id}`)) {
          // Small delay so the action modal mounts before we click the
          // deny toggle inside it.
          setTimeout(() => clickByTestId("open-deny-btn"), 120);
        }
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, rowIds, focusedIdx, helpOpen, modalOpen, onCloseModal, onFocusIndexChange, onToggleTriage]);

  if (!enabled) return null;

  return (
    <>
      {/* Discoverability button — bottom-right floating */}
      <button
        type="button"
        onClick={() => setHelpOpen((v) => !v)}
        data-testid="keyboard-shortcut-help-btn"
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5 bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 text-white text-xs font-medium px-3 py-2 rounded-full shadow-lg border border-slate-700"
        title="Keyboard shortcuts (press ? to toggle)"
      >
        <Keyboard className="w-4 h-4" />
        <span className="hidden sm:inline">Shortcuts</span>
        <kbd className="ml-1 hidden sm:inline-block px-1.5 py-0.5 rounded bg-slate-700 dark:bg-slate-600 text-[10px] font-mono">?</kbd>
      </button>

      {helpOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHelpOpen(false)}
          data-testid="keyboard-shortcut-overlay"
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Keyboard shortcuts
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setHelpOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                data-testid="keyboard-shortcut-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Shortcuts are disabled while typing in inputs. Action keys (A/D/Enter) need a focused row — use <Kbd>J</Kbd>/<Kbd>K</Kbd> first.
            </p>
            <ul className="space-y-2 text-sm">
              <ShortcutRow keys={["?"]} desc="Toggle this help overlay" />
              <ShortcutRow keys={["T"]} desc="Toggle Triage mode" />
              <ShortcutRow keys={["J", "↓"]} desc="Focus next row" />
              <ShortcutRow keys={["K", "↑"]} desc="Focus previous row" />
              <ShortcutRow keys={["Enter"]} desc="Open focused order details" />
              <ShortcutRow keys={["A"]} desc="Open Write-ERX modal (approve)" />
              <ShortcutRow keys={["D"]} desc="Open Write-ERX modal & jump to Deny panel" />
              <ShortcutRow keys={["Esc"]} desc="Close modal · release lock" />
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="inline-block px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-[10.5px] font-mono text-slate-700 dark:text-slate-200">
      {children}
    </kbd>
  );
}

function ShortcutRow({ keys, desc }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5">
        {keys.map((k, i) => (
          <span key={k} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-[10px] text-slate-400">or</span>}
            <Kbd>{k}</Kbd>
          </span>
        ))}
      </div>
      <span className="text-sm text-slate-600 dark:text-slate-300">{desc}</span>
    </li>
  );
}
